import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { title: 'Sign in · FA Forge', public: true }
  },
  {
    path: '/auth/callback',
    name: 'auth-callback',
    component: () => import('@/views/AuthCallbackView.vue'),
    meta: { title: 'Signing in… · FA Forge', public: true }
  },
  {
    path: '/',
    redirect: { name: 'requirements' }
  },
  {
    path: '/home',
    name: 'home',
    component: () => import('@/views/HomeView.vue'),
    meta: { title: 'FA Forge' }
  },
  {
    path: '/requirements',
    name: 'requirements',
    component: () => import('@/views/RequirementsView.vue'),
    meta: { title: 'Requirements · FA Forge' }
  },
  {
    path: '/requirements/new',
    name: 'requirement-new',
    component: () => import('@/views/IntakeView.vue'),
    meta: { title: 'New requirement · FA Forge' }
  },
  {
    path: '/requirements/:id',
    name: 'requirement-detail',
    component: () => import('@/views/RequirementDetailView.vue'),
    meta: { title: 'Requirement · FA Forge' },
    props: true
  },
  {
    path: '/admin/agents',
    name: 'agents',
    component: () => import('@/views/AgentsView.vue'),
    meta: { title: 'Agents · FA Forge' }
  },
  {
    path: '/admin/projects',
    name: 'projects',
    component: () => import('@/views/ProjectsView.vue'),
    meta: { title: 'Projects · FA Forge' }
  },
  {
    path: '/admin/agents/:key',
    name: 'agent-edit',
    component: () => import('@/views/AgentEditView.vue'),
    meta: { title: 'Edit agent · FA Forge' },
    props: true
  },
  {
    path: '/admin/modules',
    name: 'modules',
    component: () => import('@/views/ModulesView.vue'),
    meta: { title: 'Modules · FA Forge' }
  },
  {
    path: '/admin/repos',
    name: 'repos',
    component: () => import('@/views/ReposView.vue'),
    meta: { title: 'Repos · FA Forge' }
  },
  {
    path: '/admin/context',
    name: 'company-context',
    component: () => import('@/views/CompanyContextView.vue'),
    meta: { title: 'Company Context · FA Forge' }
  },
  {
    path: '/admin/admins',
    name: 'admins',
    component: () => import('@/views/AdminsView.vue'),
    meta: { title: 'Admins · FA Forge' }
  },
  {
    path: '/admin/costs',
    name: 'costs',
    component: () => import('@/views/CostsView.vue'),
    meta: { title: 'Cost dashboard · FA Forge' }
  },
  {
    path: '/admin/observability',
    name: 'observability',
    component: () => import('@/views/ObservabilityView.vue'),
    meta: { title: 'Observability · FA Forge' }
  },
  {
    path: '/bugsink',
    name: 'bugsink-issues',
    component: () => import('@/views/BugsinkIssuesView.vue'),
    meta: { title: 'Bugsink Issues · FA Forge' }
  },
  {
    path: '/bugsink/digest',
    name: 'bugsink-digest',
    component: () => import('@/views/BugsinkDigestView.vue'),
    meta: { title: 'Bugsink Digest · FA Forge' }
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, saved) {
    return saved ?? { top: 0 };
  }
});

// Global guard: every route except those marked { public: true } requires a valid Google
// ID token. The store reads it from localStorage on init and validates exp on every check.
router.beforeEach((to) => {
  const auth = useAuthStore();
  if (to.meta?.public) return true;
  if (!auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } };
  }
  // Admin area (/admin/*) is gated by the DB-backed allow-list. Non-admins are bounced to
  // Requirements. The API independently enforces this with [Authorize(Roles="Admin")], so this
  // is convenience, not the security boundary.
  if (to.path.startsWith('/admin') && !auth.isAdmin) {
    return { name: 'requirements' };
  }
  return true;
});

router.afterEach((to) => {
  const title = to.meta.title as string | undefined;
  if (title) document.title = title;
});

export default router;
